import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { aiConfigService } from '../../../services/aiConfigService';
import type { ResponseFormatRow } from '../config/columns';

export const useResponseFormat = () => {
  const [treeData, setTreeData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<ResponseFormatRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ResponseFormatRow | null>(null);
  const [formRef, setFormRef] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  const loadTreeData = useCallback(async () => {
    try {
      // TODO: 使用 mock 数据，后续替换为真实 API
      // 返回格式独立管理，按格式类型分组
      const mockFormats = [
        { id: '1', formatId: 'markdown-standard', formatType: 'markdown' },
        { id: '2', formatId: 'json-simple', formatType: 'json' },
        { id: '3', formatId: 'text-plain', formatType: 'text' },
      ];
      
      // 按格式类型分组
      const formatTypes = ['markdown', 'json', 'text', 'table', 'list'];
      const tree = formatTypes.map((type) => {
        const formats = mockFormats.filter((f) => f.formatType === type);
        return {
          key: `format-type:${type}`,
          title: `${type.toUpperCase()} 格式`,
          type: 'format-type',
          children: formats.map((rf: any) => ({
            key: `response-format:${rf.id}`,
            title: `${rf.formatId}`,
            type: 'response-format',
            data: rf,
            isLeaf: true,
          })),
        };
      }).filter(node => node.children.length > 0);
      
      setTreeData(tree);
    } catch (err: any) {
      message.error(`加载树形数据失败: ${err?.message}`);
    }
  }, []);

  const loadTableData = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: 使用 mock 数据，后续替换为真实 API
      // const formats = await aiConfigService.getResponseFormatList();
      const mockFormats: ResponseFormatRow[] = [
        {
          id: '1',
          formatId: 'markdown-standard',
          formatType: 'markdown',
          description: '标准 Markdown 格式',
          isActive: true,
        },
        {
          id: '2',
          formatId: 'json-simple',
          formatType: 'json',
          description: '简单 JSON 格式',
          isActive: true,
        },
        {
          id: '3',
          formatId: 'text-plain',
          formatType: 'text',
          description: '纯文本格式',
          isActive: true,
        },
      ];
      setTableData(mockFormats);
    } catch (err: any) {
      message.error(`加载数据失败: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = useCallback((keys: React.Key[]) => {
    if (keys.length === 0) {
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(keys);
  }, []);

  const handleOpenModal = useCallback((record?: ResponseFormatRow | null) => {
    setEditingRecord(record ?? null);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setEditingRecord(null);
    formRef?.resetFields?.();
  }, [formRef]);

  const handleSave = useCallback(async () => {
    if (!formRef) return;
    try {
      const values = await formRef.validateFields();
      const payload = {
        ...editingRecord,
        ...values,
        // 约束条件：如果是字符串，尝试解析为对象
        constraints: values.constraints
          ? typeof values.constraints === 'string'
            ? JSON.parse(values.constraints)
            : values.constraints
          : undefined,
        // 正面词和负面词：已经是数组格式（TagInput 返回的），直接使用
        positiveWords: Array.isArray(values.positiveWords)
          ? values.positiveWords
          : values.positiveWords
          ? JSON.parse(values.positiveWords)
          : undefined,
        negativeWords: Array.isArray(values.negativeWords)
          ? values.negativeWords
          : values.negativeWords
          ? JSON.parse(values.negativeWords)
          : undefined,
      };
      // TODO: 使用 mock 数据，后续替换为真实 API
      console.log('保存返回格式（Mock）:', payload);
      // await aiConfigService.upsertResponseFormat(payload);
      message.success('保存成功（Mock 模式）');
      handleCloseModal();
      loadTableData();
      loadTreeData();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
  }, [formRef, editingRecord, handleCloseModal, loadTableData, loadTreeData]);

  const handleDelete = useCallback(
    async (_id: string) => {
      try {
        // TODO: 实现删除 API
        message.success('删除成功');
        loadTableData();
        loadTreeData();
      } catch (err: any) {
        message.error(err?.message || '删除失败');
      }
    },
    [loadTableData, loadTreeData]
  );

  useEffect(() => {
    loadTreeData();
    loadTableData();
  }, [loadTreeData, loadTableData]);

  return {
    treeData,
    tableData,
    loading,
    modalVisible,
    editingRecord,
    setFormRef,
    expandedKeys,
    selectedKeys,
    loadTreeData,
    loadTableData,
    handleSelect,
    handleOpenModal,
    handleCloseModal,
    handleSave,
    handleDelete,
    setExpandedKeys,
  };
};
